/**
 * getServerData (SSR) cevaplarının Netlify CDN önbelleği.
 *
 * Önbelleksiz her doğrudan giriş (Google, Telegram linki, F5) Netlify fonksiyonunu çalıştırıyordu: ilk bayt sıcakken
 * ~1,5 sn, fonksiyon uyuyorsa ~5 sn (2026-10-30 ölçümü). Şimdi:
 * - 60 sn içinde gelenler önbellekten alır (fonksiyon çalışmaz),
 * - sonraki 1 gün içinde gelen ilk istek eskiyi ANINDA alır, Netlify arka planda yenisini üretir (kimse beklemez),
 * - `durable`: Netlify'ın tüm noktaları aynı önbelleği paylaşır.
 * Liste en fazla bir süre eski gelebilir; sayfalar açılışta listeyi zaten istemciden tazeliyor (hooks/useTazeVeri).
 * Sadece Netlify CDN'i içindir, tarayıcı önbelleğini etkilemez. Deploy önbelleği siler.
 */
export const SSR_ONBELLEK_BASLIKLARI = {
    "Netlify-CDN-Cache-Control": "public, durable, s-maxage=60, stale-while-revalidate=86400",
};

/**
 * İndirim detay sayfası (card-detail?oid=): bir indirim kaydı paylaşıldıktan sonra pek değişmez, ama "devam ediyor /
 * sona erdi" durumu ve admin düzenlemeleri yansısın diye liste sayfalarından kısa: 5 dk taze, sonraki 1 saat içinde
 * gelen ilk istek eskiyi anında alır ve arkada yenilenir. Telegram paylaşımından sonraki tıklama dalgası önbellekten
 * karşılanır. Önbellek anahtarı sadece `oid` (`name` vb. parametreler aynı sayfayı paylaşır).
 */
export const SSR_DETAY_ONBELLEK_BASLIKLARI = {
    "Netlify-CDN-Cache-Control": "public, durable, s-maxage=300, stale-while-revalidate=3600",
    "Netlify-Vary": "query=oid",
};

/**
 * Site içi geçiş (navigate / Link) mi? Gatsby geçişte sayfanın verisini `/page-data/<yol>/page-data.json` adresinden
 * ister; Netlify bunu da aynı SSR fonksiyonuna verir ve getServerData'ya bu adres `url` olarak gelir
 * (node_modules/gatsby/dist/utils/get-server-data.js). Doğrudan girişte (link, Google, F5) `url` sayfanın kendisidir.
 *
 * Geçişte getServerData Azure'a GİTMEZ, boş döner (2026-11-03): sayfa bileşenleri veriyi zaten tarayıcıdan Azure'a sorup
 * alıyor (useTazeVeri, UrunDetay, SayfaliIndirimListesi). Eskiden aynı veri iki kez çekiliyor ve Gatsby, Netlify'ın
 * çektiğini beklediği için tıklamadan sonra ekran 0,6-1,5 sn (fonksiyon uyuyorsa 5-8 sn) değişmiyordu. Şimdi sayfa hemen
 * açılır, iskelet görünür, veri tarayıcıdan gelir. Doğrudan giriş (SEO, önizleme) değişmedi: dolu HTML, veri HTML'e gömülü
 * (window.pageData) olduğu için tarayıcı açılışta page-data istemez.
 * Önceki deneme (ayrı /indirim/ sayfası) geri alınmıştı; bu yolda adresler ve sayfalar aynı.
 */
export function istemciGecisiMi(url) {
    return typeof url === "string" && url.indexOf("/page-data/") !== -1;
}

/**
 * Geçişin boş cevabı herkese ve her oid'ye aynı: Netlify CDN'inde 7 gün tutulur, fonksiyon her sayfa şablonu için deploy
 * sonrası bir kez çalışır (deploy önbelleği siler). Gatsby page-data adresine sorguyu da ekliyor
 * (`/page-data/h-card-detail/page-data.json?oid=123`) ve fonksiyon cevaplarında sorgu varsayılan olarak önbellek
 * anahtarına giriyor; `Netlify-Vary: query=<liste>` anahtara SADECE listelenenleri koyar (docs.netlify.com/platform/caching,
 * örnek: query=style|season iken /shirts?price=low ile /shirts aynı kopya). `iu_surum` adreslerde hiç yok → tüm oid'ler
 * tek kopyayı paylaşır.
 */
const GECIS_ONBELLEK_BASLIKLARI = {
    "Netlify-CDN-Cache-Control": "public, durable, s-maxage=604800",
    "Netlify-Vary": "query=iu_surum",
};

/** getServerData'nın site içi geçişteki cevabı. */
export function istemciGecisiCevabi() {
    return { headers: GECIS_ONBELLEK_BASLIKLARI, props: {} };
}

/**
 * SSR (getServerData) isteklerinin başlıkları. API'ler Cloudflare arkasındayken Netlify'ın sunucu fonksiyonları az
 * sayıda ortak IP'den gelir ve IP başına hız sınırına takılabilir; Netlify ortam değişkeni IU_SSR_ANAHTAR tanımlıysa
 * istek bu gizli başlıkla gider, Cloudflare kuralı bu başlığı taşıyanları hız sınırından muaf tutar. Değişken
 * GATSBY_ önekli DEĞİL: tarayıcıya hiç gönderilmez, sadece sunucu tarafında okunur.
 */
export function sunucuBasliklari() {
    const b = { 'Content-type': 'application/json' };
    if (typeof process !== "undefined" && process.env && process.env.IU_SSR_ANAHTAR) {
        b["X-IU-SSR"] = process.env.IU_SSR_ANAHTAR;
    }
    return b;
}

/**
 * SSR isteğinin zaman aşımı (2026-11-05). Azure uyurken ilk istek 15-25 sn sürüyor; zaman aşımı yokken Netlify fonksiyonu
 * kendi süresi dolunca 5xx dönüyordu ve Google bu sayfaları "Sunucu hatası (5xx)" diye dizine almıyordu (Search Console).
 * Süre dolarsa istek kesilir, sayfa verisiz ama 200 ile açılır (başlık, açıklama, H1 yerinde), liste tarayıcıdan gelir.
 */
export function ssrSinyali(ms = 5000) {
    return typeof AbortSignal !== "undefined" && AbortSignal.timeout ? AbortSignal.timeout(ms) : undefined;
}
